// 36-month profitability and cash trajectory chart. SVG-native (no chart lib).
// Defaults to monthly operating profit so investors see the path to breakeven.
// All three scenarios overlay; active is bold, others faint.

import { useState } from 'react';

const W = 1080;
const H = 380;
const PAD_L = 60;
const PAD_R = 24;
const PAD_T = 30;
const PAD_B = 38;

const SERIES = ['conservative', 'base', 'bull'];

const PALETTE = {
  conservative: '#FB7185',
  base: '#1F1F1F',
  bull: '#78C24A',
};

const SERIES_LABEL = {
  conservative: 'Pessimistic',
  base: 'Mean',
  bull: 'Optimistic',
};

const METRICS = {
  netPL: {
    label: 'Monthly operating profit',
    value: (row) => row.netPL ?? 0,
  },
  postRaiseCash: {
    label: 'Post-raise cash balance',
    value: (row, raiseAmount) => (row.cumCash ?? 0) + raiseAmount,
  },
  cumCash: {
    label: 'Pre-financing cumulative burn',
    value: (row) => row.cumCash ?? 0,
  },
};

function fmtCompact(n) {
  const sign = n < 0 ? '−' : '';
  const a = Math.abs(n);
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(1)}k`;
  return `${sign}$${Math.round(a)}`;
}

function niceStep(s) {
  const exp = Math.pow(10, Math.floor(Math.log10(Math.abs(s) || 1)));
  const f = Math.abs(s) / exp;
  let nf;
  if (f < 1.5) nf = 1;
  else if (f < 3) nf = 2;
  else if (f < 7) nf = 5;
  else nf = 10;
  return nf * exp;
}

function niceTicks(min, max, count) {
  const range = max - min;
  if (range === 0) return [min];
  const step = niceStep(range / count);
  const ticks = [];
  let t = Math.ceil(min / step) * step;
  while (t <= max + 1e-6) {
    ticks.push(t);
    t += step;
  }
  return ticks;
}

const X_TICKS = [1, 6, 12, 18, 24, 30, 36];

export default function CumCashChart({ rowsByScenario, active, raiseAmount = 4_000_000 }) {
  const [metric, setMetric] = useState('netPL');
  const metricDef = METRICS[metric];
  const valueOf = (row) => metricDef.value(row, raiseAmount);

  const safeRows = (k) => (Array.isArray(rowsByScenario?.[k]) ? rowsByScenario[k] : []);

  const allValues = SERIES.flatMap((s) => safeRows(s).map(valueOf));
  if (allValues.length === 0) {
    return (
      <section className="rounded-card border border-carbon-100 bg-white p-7">
        <p className="caption text-carbon-400 mb-2">Profitability and cash trajectory</p>
        <p className="body text-carbon-500">No forecast rows available yet.</p>
      </section>
    );
  }

  const minY = Math.min(0, ...allValues);
  const maxY = Math.max(0, ...allValues);
  const yPad = (maxY - minY) * 0.08 || 1;
  const y0 = minY - yPad;
  const y1 = maxY + yPad;

  const xOf = (m) => PAD_L + ((m - 1) / 35) * (W - PAD_L - PAD_R);
  const yOf = (v) => PAD_T + (1 - (v - y0) / (y1 - y0)) * (H - PAD_T - PAD_B);

  const linePath = (rows) =>
    rows.map((r, i) => `${i === 0 ? 'M' : 'L'}${xOf(r.month)},${yOf(valueOf(r))}`).join(' ');

  const yTicks = niceTicks(y0, y1, 5);
  const activeRows = safeRows(active);
  const lastActive = activeRows[activeRows.length - 1];

  return (
    <section className="rounded-card border border-carbon-100 bg-white p-7">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
        <p className="caption text-carbon-400">Profitability and cash trajectory</p>
        <MetricToggle metric={metric} onChange={setMetric} />
      </div>

      <Legend active={active} />

      <div className="overflow-x-auto nice-scroll">
        <svg
          width="100%"
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="xMidYMid meet"
          className="block"
          style={{ minWidth: 720 }}
          role="img"
          aria-label={`${METRICS[metric].label} across 36 months, all scenarios`}
        >
          {yTicks.map((t) => (
            <g key={`y${t}`}>
              <line
                x1={PAD_L}
                y1={yOf(t)}
                x2={W - PAD_R}
                y2={yOf(t)}
                stroke="#EBEBEB"
                strokeWidth="1"
              />
              <text
                x={PAD_L - 8}
                y={yOf(t) + 4}
                textAnchor="end"
                fill="#757575"
                fontSize="11"
                fontFamily="DM Sans, sans-serif"
              >
                {fmtCompact(t)}
              </text>
            </g>
          ))}

          {y0 < 0 && y1 > 0 && (
            <line
              x1={PAD_L}
              y1={yOf(0)}
              x2={W - PAD_R}
              y2={yOf(0)}
              stroke="#1F1F1F"
              strokeWidth="1.5"
              strokeDasharray="3 4"
            />
          )}

          {X_TICKS.map((m) => (
            <g key={`x${m}`}>
              <line
                x1={xOf(m)}
                y1={H - PAD_B}
                x2={xOf(m)}
                y2={H - PAD_B + 5}
                stroke="#D4D4D4"
                strokeWidth="1"
              />
              <text
                x={xOf(m)}
                y={H - PAD_B + 22}
                textAnchor="middle"
                fill="#757575"
                fontSize="11"
                fontFamily="DM Sans, sans-serif"
              >
                M{m}
              </text>
            </g>
          ))}

          {SERIES.filter((s) => s !== active).map((s) => {
            const rows = safeRows(s);
            if (!rows.length) return null;
            return (
              <path
                key={s}
                d={linePath(rows)}
                fill="none"
                stroke={PALETTE[s]}
                strokeWidth="2"
                strokeOpacity="0.32"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            );
          })}

          {activeRows.length > 0 && (
            <path
              d={linePath(activeRows)}
              fill="none"
              stroke={PALETTE[active]}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}

          {lastActive && (
            <circle
              cx={xOf(lastActive.month)}
              cy={yOf(valueOf(lastActive))}
              r="5"
              fill={PALETTE[active]}
              stroke="#fff"
              strokeWidth="2"
            />
          )}
        </svg>
      </div>
    </section>
  );
}

function Legend({ active }) {
  return (
    <div className="flex items-center gap-5 mb-4 flex-wrap">
      {SERIES.map((s) => {
        const isActive = active === s;
        return (
          <div key={s} className="flex items-center gap-2">
            <svg width="22" height="3" aria-hidden="true">
              <line
                x1="0"
                y1="1.5"
                x2="22"
                y2="1.5"
                stroke={PALETTE[s]}
                strokeWidth={isActive ? 3 : 2}
                strokeOpacity={isActive ? 1 : 0.5}
              />
            </svg>
            <span className={`label ${isActive ? 'text-carbon-800' : 'text-carbon-400'}`}>
              {SERIES_LABEL[s]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function MetricToggle({ metric, onChange }) {
  return (
    <div
      role="radiogroup"
      aria-label="Chart metric"
      className="inline-flex p-1 rounded-full bg-carbon-50 border border-carbon-100"
    >
      {Object.entries(METRICS).map(([k, m]) => {
        const active = metric === k;
        return (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(k)}
            className={`label rounded-full transition-colors h-7 px-3 outline-none focus-visible:ring-2 focus-visible:ring-ice-400 ${
              active
                ? 'bg-white text-carbon-800 shadow-sm'
                : 'text-carbon-500 hover:text-carbon-800'
            }`}
          >
            {m.label}
          </button>
        );
      })}
    </div>
  );
}
